import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ScrollingText from './components/ScrollingText';
import TextInput from './components/TextInput';
import UrlParamViewer from './components/UrlParamViewer';
import './styles/App.css';
import './styles/UrlParamViewer.css';

function App() {
  const [scrollingContent, setScrollingContent] = useState('Welcome to the Scrolling Text Builder! :news: Breaking News: *Important announcement* from the _city council_ |div| |weather[London,compact,c]| Current weather information |div| Use formatting codes for cool effects!');
  const [speed, setSpeed] = useState(5);
  const [backgroundColor, setBackgroundColor] = useState('#2c3e50');
  const [backgroundAlpha, setBackgroundAlpha] = useState(1);
  const [textColor, setTextColor] = useState('#ffffff');
  const [scale, setScale] = useState(1);
  const [fontFamily, setFontFamily] = useState('Roboto');
  const [weatherApiKey, setWeatherApiKey] = useState(process.env.REACT_APP_WEATHER_API_KEY || '');
  const textInputRef = useRef(null);

  // Time zone options for the world time widget
  const timeZones = [
    { name: 'UTC', identifier: 'UTC', region: 'utc' },
    // North America
    { name: 'New York (US Eastern)', identifier: 'America/New_York', region: 'us' },
    { name: 'Chicago (US Central)', identifier: 'America/Chicago', region: 'us' },
    { name: 'Denver (US Mountain)', identifier: 'America/Denver', region: 'us' },
    { name: 'Los Angeles (US Pacific)', identifier: 'America/Los_Angeles', region: 'us' },
    { name: 'Toronto (Canada)', identifier: 'America/Toronto', region: 'us' },
    { name: 'Mexico City (Mexico)', identifier: 'America/Mexico_City', region: 'us' },
    // South America
    { name: 'São Paulo (Brazil)', identifier: 'America/Sao_Paulo', region: 'us' },
    { name: 'Buenos Aires (Argentina)', identifier: 'America/Argentina/Buenos_Aires', region: 'us' },
    { name: 'Santiago (Chile)', identifier: 'America/Santiago', region: 'us' },
    // Europe
    { name: 'London (UK)', identifier: 'Europe/London', region: 'eu' },
    { name: 'Paris (France)', identifier: 'Europe/Paris', region: 'eu' },
    { name: 'Berlin (Germany)', identifier: 'Europe/Berlin', region: 'eu' },
    { name: 'Madrid (Spain)', identifier: 'Europe/Madrid', region: 'eu' },
    { name: 'Rome (Italy)', identifier: 'Europe/Rome', region: 'eu' },
    { name: 'Moscow (Russia)', identifier: 'Europe/Moscow', region: 'eu' },
    // Africa
    { name: 'Cairo (Egypt)', identifier: 'Africa/Cairo', region: 'eu' },
    { name: 'Lagos (Nigeria)', identifier: 'Africa/Lagos', region: 'eu' },
    { name: 'Johannesburg (South Africa)', identifier: 'Africa/Johannesburg', region: 'eu' },
    { name: 'Nairobi (Kenya)', identifier: 'Africa/Nairobi', region: 'eu' },
    // Middle East
    { name: 'Dubai (UAE)', identifier: 'Asia/Dubai', region: 'asia' },
    { name: 'Istanbul (Turkey)', identifier: 'Europe/Istanbul', region: 'asia' },
    { name: 'Jerusalem (Israel)', identifier: 'Asia/Jerusalem', region: 'asia' },
    { name: 'Riyadh (Saudi Arabia)', identifier: 'Asia/Riyadh', region: 'asia' },
    // Asia
    { name: 'Mumbai (India)', identifier: 'Asia/Kolkata', region: 'asia' },
    { name: 'Beijing (China)', identifier: 'Asia/Shanghai', region: 'asia' },
    { name: 'Tokyo (Japan)', identifier: 'Asia/Tokyo', region: 'asia' },
    { name: 'Seoul (South Korea)', identifier: 'Asia/Seoul', region: 'asia' },
    { name: 'Singapore', identifier: 'Asia/Singapore', region: 'asia' },
    { name: 'Bangkok (Thailand)', identifier: 'Asia/Bangkok', region: 'asia' },
    // Oceania
    { name: 'Sydney (Australia)', identifier: 'Australia/Sydney', region: 'aus' },
    { name: 'Melbourne (Australia)', identifier: 'Australia/Melbourne', region: 'aus' },
    { name: 'Perth (Australia)', identifier: 'Australia/Perth', region: 'aus' },
    { name: 'Auckland (New Zealand)', identifier: 'Pacific/Auckland', region: 'aus' },
    { name: 'Honolulu (Hawaii)', identifier: 'Pacific/Honolulu', region: 'aus' }
  ];

  // Available Google Fonts
  const availableFonts = [
    'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Oswald', 
    'Raleway', 'Poppins', 'Nunito', 'Playfair Display', 'Dancing Script',
    'Pacifico', 'Quicksand', 'Ubuntu', 'Rubik', 'Source Sans Pro'
  ];

  // Preload all fonts for the dropdown
  useEffect(() => {
    // Create a link for Google Fonts with all available fonts
    const fontsList = availableFonts.map(font => 
      font.replace(/\s+/g, '+') + ':400,700'
    ).join('|');
    
    const link = document.createElement('link');
    link.href = `https://fonts.googleapis.com/css2?family=${fontsList}&display=swap`;
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  // Log the API key for debugging (remove in production)
  useEffect(() => {
    console.log('Weather API Key from env:', weatherApiKey ? 'Available' : 'Not available');
  }, [weatherApiKey]);

  const handleTextSubmit = (text) => {
    setScrollingContent(text);
  };

  const handleSpeedChange = (e) => {
    setSpeed(parseInt(e.target.value));
  };

  const handleBackgroundColorChange = (e) => {
    setBackgroundColor(e.target.value);
  };

  const handleBackgroundAlphaChange = (e) => {
    setBackgroundAlpha(parseFloat(e.target.value));
  };

  const handleTextColorChange = (e) => {
    setTextColor(e.target.value);
  };

  const handleScaleChange = (e) => {
    setScale(parseFloat(e.target.value));
  };

  const handleFontFamilyChange = (e) => {
    setFontFamily(e.target.value);
  };

  // Process the background color with alpha
  const processedBgColor = (() => {
    // If the color is in hex format (#RRGGBB), convert to rgba
    if (backgroundColor.startsWith('#')) {
      const r = parseInt(backgroundColor.slice(1, 3), 16);
      const g = parseInt(backgroundColor.slice(3, 5), 16);
      const b = parseInt(backgroundColor.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${backgroundAlpha})`;
    }
    return backgroundColor;
  })();

  // Component for the home page (editor)
  const HomePage = () => (
    <div className="container">
      <div className="app-header">
        <img src="/logo.png" alt="Scrolling Text Builder Logo" className="app-logo" />
        <h1 className="app-title">Scrolling Text Builder</h1>
      </div>
      
      <div className="card">
        <TextInput 
          onSubmit={handleTextSubmit} 
          initialText={scrollingContent} 
          ref={textInputRef}
          timeZones={timeZones}
        />
        
        <div className="controls">
          <div className="control-group">
            <label>
              Speed:
              <input 
                type="range" 
                min="1" 
                max="10" 
                value={speed} 
                onChange={handleSpeedChange}
                className="slider"
              />
              <span>{speed}</span>
            </label>
            <span className="control-help">1 = Very Slow, 10 = Fast</span>
          </div>
          
          <div className="control-group">
            <label>
              Background Color:
              <input 
                type="color" 
                value={backgroundColor} 
                onChange={handleBackgroundColorChange}
              />
            </label>
          </div>
          
          <div className="control-group">
            <label>
              Background Opacity:
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.1" 
                value={backgroundAlpha} 
                onChange={handleBackgroundAlphaChange}
                className="slider"
              />
              <span>{backgroundAlpha}</span>
            </label>
          </div>
          
          <div className="control-group">
            <label>
              Text Color:
              <input 
                type="color" 
                value={textColor} 
                onChange={handleTextColorChange}
              />
            </label>
          </div>

          <div className="control-group">
            <label>
              Scale (Size):
              <input 
                type="range" 
                min="0.5" 
                max="3" 
                step="0.1" 
                value={scale} 
                onChange={handleScaleChange}
                className="slider"
              />
              <span>{scale}x</span>
            </label>
          </div>

          <div className="control-group">
            <label>
              Font:
              <select 
                value={fontFamily} 
                onChange={handleFontFamilyChange}
                className="font-select"
              >
                {availableFonts.map(font => (
                  <option key={font} value={font}>{font}</option>
                ))}
              </select>
            </label>
          </div>
        </div>
      </div>
      
      <div className="help-card card">
        <h3 className="theme-heading">Formatting Guide</h3>
        
        <div className="formatting-sections">
          <div className="formatting-section">
            <h4>Text Styling</h4>
            <ul>
              <li><code>*bold*</code> <span>Makes text <strong>bold</strong></span></li>
              <li><code>_italic_</code> <span>Makes text <em>italic</em></span></li>
              <li><code>~blink~</code> <span>Creates blinking text</span></li>
              <li><code>|h[text]|</code> <span>Creates a highlighted box around text</span></li>
              <li><code>|pulse[text]|</code> <span>Creates pulsing animated text</span></li>
            </ul>
          </div>
          
          <div className="formatting-section">
            <h4>Colors & Sizes</h4>
            <ul>
              <li><code>#color[text]</code> <span>Colors text (e.g., <code>#red[text]</code> or <code>#FF5733[text]</code>)</span></li>
              <li><code>^small[text]</code> <span>Smaller text size</span></li>
              <li><code>^medium[text]</code> <span>Normal text size</span></li>
              <li><code>^large[text]</code> <span>Larger text size</span></li>
            </ul>
          </div>
          
          <div className="formatting-section">
            <h4>Layout & Media</h4>
            <ul>
              <li><code>|div|</code> <span>Inserts a vertical divider</span></li>
              <li><code>|space|</code> <span>Inserts extra space</span></li>
              <li><code>!img[url]</code> <span>Inserts an image</span></li>
            </ul>
          </div>
          
          <div className="formatting-section">
            <h4>Emoji Shortcuts</h4>
            <ul className="emoji-list">
              <li><code>:smile:</code> <span>😊</span> <code>:heart:</code> <span>❤️</span> <code>:star:</code> <span>⭐</span></li>
              <li><code>:warning:</code> <span>⚠️</span> <code>:fire:</code> <span>🔥</span> <code>:thumbsup:</code> <span>👍</span></li>
              <li><code>:clock:</code> <span>🕒</span> <code>:news:</code> <span>📰</span> <code>:mic:</code> <span>🎤</span> <code>:tv:</code> <span>📺</span></li>
            </ul>
          </div>

          <div className="formatting-section">
            <h4>World Time Widgets</h4>
            <ul>
              <li><code>|time[America/New_York,New York,12h]|</code> <span>Creates a live New York time widget</span></li>
              <li><code>|time[UTC]|</code> <span>Shows UTC time (minimal format)</span></li>
              <li><code>|time[Europe/London,London,24h]|</code> <span>London time in 24-hour format</span></li>
            </ul>
            <p>Use the World Time section under the Emojis toolbar to add time widgets with proper timezone IDs.</p>
          </div>
          
          <div className="formatting-section">
            <h4>Weather Widgets</h4>
            <ul>
              <li><code>|weather[New York]|</code> <span>Shows weather for New York (compact format)</span></li>
              <li><code>|weather[London,full,c]|</code> <span>Full weather details for London in Celsius</span></li>
              <li><code>|weather[Tokyo,icon,f]|</code> <span>Icon-only weather for Tokyo in Fahrenheit</span></li>
            </ul>
            <p>Requires a WeatherAPI.com API key provided by the OBS script. Formats: compact (default), full, icon. Units: c (Celsius), f (Fahrenheit).</p>
          </div>
        </div>
        
        <div className="formatting-section">
          <h4>Example:</h4>
          <code>:news: Breaking News: *Important announcement* from the _city council_. |div| #red[Warning:] Heavy traffic expected on Main Street. |div| |h[Stay tuned]| for ~more updates~ throughout the day.</code>
        </div>
        
        <div className="example-section">
          <h4>Enhanced Effects Example:</h4>
          <code>|pulse[LIVE]| |h[#d4af37[*Special Report*]]| |div| ~Breaking News~ from the |pulse[City Center]| |div| :warning: Stay tuned for #red[updates]!</code>
        </div>
        
        <div className="example-section">
          <h4>World Time Example:</h4>
          <code>:news: *Global Update* |div| Local: |time[America/New_York,NYC,12h]| |div| London: |time[Europe/London]| |div| Tokyo: |time[Asia/Tokyo,Tokyo,24h]|</code>
        </div>
        
        <div className="example-section">
          <h4>Weather Example:</h4>
          <code>:news: *Weather Alert* |div| Local: |weather[New York,compact,f]| |div| |weather[London,full,c]| |div| |weather[Tokyo,icon,c]|</code>
        </div>
        
        <div className="generate-url-section">
          <h4>Share Your Ticker:</h4>
          <p>Generate a URL with your current settings to share or embed:</p>
          <button 
            className="generate-url-button"
            onClick={() => {
              const params = new URLSearchParams();
              params.set('text', scrollingContent);
              params.set('speed', speed);
              params.set('bgColor', backgroundColor);
              params.set('bgAlpha', backgroundAlpha);
              params.set('textColor', textColor);
              params.set('scale', scale);
              params.set('font', fontFamily);
              const url = `${window.location.origin}/url-view?${params.toString()}`;
              
              // Copy to clipboard
              navigator.clipboard.writeText(url)
                .then(() => {
                  alert('URL copied to clipboard!');
                })
                .catch(() => {
                  alert('URL: ' + url);
                });
            }}
          >
            Generate & Copy URL
          </button>
        </div>
      </div>
      
      <div className="preview-container">
        <h3 className="preview-heading theme-heading">Live Preview</h3>
        <div className="preview-box">
          <ScrollingText 
            content={scrollingContent}
            speed={speed}
            backgroundColor={processedBgColor}
            textColor={textColor}
            scale={scale}
            fontFamily={fontFamily}
            weatherApiKey={weatherApiKey}
          />
        </div>
      </div>
    </div>
  );

  // Component for the viewer page
  const ViewerPage = () => (
    <UrlParamViewer />
  );

  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/viewer" element={<ViewerPage />} />
        <Route path="/url-view" element={<ViewerPage />} />
      </Routes>
    </Router>
  );
}

export default App; 